import { Controller } from "@hotwired/stimulus"

// LOC-091: zoom and position a profile picture inside the round frame. The
// member picks a file (or chooses Adjust to re-open the uncropped original),
// drags the photo or nudges it with the arrow keys, and zooms with the slider.
// The box is posted as zoom (1 to 3) and the centre as fractions (0 to 1) of
// the photo, the same maths as Tenants::Locals::AvatarCropBox, which renders the
// square avatar on save. Without this controller the crop fields stay disabled
// and the upload posts as it always did.
const MIN_ZOOM = 1
const MAX_ZOOM = 3
const NUDGE = 0.02 // of the frame per arrow key press, ten times that with Shift

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)
const round4 = (value) => Number(value.toFixed(4))

export default class extends Controller {
  static targets = ["file", "adjust", "editor", "frame", "image", "zoom", "zoomField", "xField", "yField"]
  static values = { originalUrl: String }

  connect() {
    this.saved = this.readFields()
    if (this.hasAdjustTarget) this.adjustTarget.hidden = false
  }

  disconnect() {
    this.release()
    this.revoke()
  }

  // A new file: start centred at 1x.
  pick() {
    this.revoke()
    const file = this.fileTarget.files && this.fileTarget.files[0]
    if (!file) {
      this.close()
      return
    }
    this.objectUrl = URL.createObjectURL(file)
    this.open(this.objectUrl, { zoom: MIN_ZOOM, x: 0.5, y: 0.5 })
  }

  // The stored original at the saved box. Drops a picked file, so the posted box
  // always belongs to the photo on screen.
  adjust() {
    if (!this.originalUrlValue) return
    this.fileTarget.value = ""
    this.revoke()
    this.open(this.originalUrlValue, Object.assign({}, this.saved))
  }

  loaded() {
    const image = this.imageTarget
    if (!image.naturalWidth || !image.naturalHeight) return
    this.natural = { width: image.naturalWidth, height: image.naturalHeight }
    this.render()
  }

  zoomed() {
    if (!this.box) return
    this.box.zoom = clamp(parseFloat(this.zoomTarget.value) || MIN_ZOOM, MIN_ZOOM, MAX_ZOOM)
    this.render()
  }

  nudge(event) {
    const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }
    const direction = directions[event.key]
    if (!direction || !this.natural) return

    event.preventDefault()
    const step = (event.shiftKey ? NUDGE * 10 : NUDGE) * this.frameTarget.clientWidth
    this.moveBy(direction[0] * step, direction[1] * step)
  }

  grab(event) {
    if (!this.natural || event.button > 0) return

    event.preventDefault()
    this.frameTarget.focus()
    this.release()
    let last = { x: event.clientX, y: event.clientY }
    const frame = this.frameTarget
    if (frame.setPointerCapture) frame.setPointerCapture(event.pointerId)
    frame.style.cursor = "grabbing"

    this.onMove = (move) => {
      this.moveBy(move.clientX - last.x, move.clientY - last.y)
      last = { x: move.clientX, y: move.clientY }
    }
    this.onUp = () => this.release()
    frame.addEventListener("pointermove", this.onMove)
    frame.addEventListener("pointerup", this.onUp)
    frame.addEventListener("pointercancel", this.onUp)
  }

  // A visible focus ring without a stylesheet (Locals UI is inline-styled).
  focusRing(event) {
    const focused = event.type === "focus"
    this.frameTarget.style.outline = focused ? "3px solid #1c1c1e" : ""
    this.frameTarget.style.outlineOffset = focused ? "3px" : ""
  }

  // private

  open(url, box) {
    this.box = box
    this.natural = null
    this.editorTarget.hidden = false
    this.fieldTargets().forEach((field) => { field.disabled = false })
    this.zoomTarget.value = box.zoom
    this.writeFields()

    const image = this.imageTarget
    if (image.getAttribute("src") === url && image.complete) {
      this.loaded()
    } else {
      image.src = url
    }
  }

  close() {
    this.box = null
    this.natural = null
    this.editorTarget.hidden = true
    this.fieldTargets().forEach((field) => { field.disabled = true })
  }

  // Moves the photo by a distance in screen pixels (drag right, photo goes right).
  moveBy(dx, dy) {
    const { width, height } = this.displaySize()
    this.box.x -= dx / width
    this.box.y -= dy / height
    this.render()
  }

  displaySize() {
    const frame = this.frameTarget.clientWidth
    const scale = (frame * this.box.zoom) / Math.min(this.natural.width, this.natural.height)
    return { frame, width: this.natural.width * scale, height: this.natural.height * scale }
  }

  render() {
    if (!this.box || !this.natural) {
      this.writeFields()
      return
    }

    // Keep the photo covering the frame: the centre cannot come closer to an
    // edge than half the visible square.
    const side = Math.min(this.natural.width, this.natural.height) / this.box.zoom
    const halfX = side / (2 * this.natural.width)
    const halfY = side / (2 * this.natural.height)
    this.box.x = clamp(this.box.x, halfX, 1 - halfX)
    this.box.y = clamp(this.box.y, halfY, 1 - halfY)

    const { frame, width, height } = this.displaySize()
    const image = this.imageTarget
    image.style.width = `${width}px`
    image.style.height = `${height}px`
    image.style.transform = `translate(${frame / 2 - this.box.x * width}px, ${frame / 2 - this.box.y * height}px)`
    this.writeFields()
  }

  readFields() {
    return {
      zoom: clamp(this.number(this.zoomFieldTarget.value, MIN_ZOOM), MIN_ZOOM, MAX_ZOOM),
      x: clamp(this.number(this.xFieldTarget.value, 0.5), 0, 1),
      y: clamp(this.number(this.yFieldTarget.value, 0.5), 0, 1)
    }
  }

  number(raw, fallback) {
    const value = parseFloat(raw)
    return Number.isFinite(value) ? value : fallback
  }

  writeFields() {
    if (!this.box) return
    this.zoomFieldTarget.value = round4(this.box.zoom)
    this.xFieldTarget.value = round4(this.box.x)
    this.yFieldTarget.value = round4(this.box.y)
  }

  fieldTargets() {
    return [this.zoomFieldTarget, this.xFieldTarget, this.yFieldTarget]
  }

  release() {
    if (!this.onMove) return
    const frame = this.frameTarget
    frame.removeEventListener("pointermove", this.onMove)
    frame.removeEventListener("pointerup", this.onUp)
    frame.removeEventListener("pointercancel", this.onUp)
    frame.style.cursor = "grab"
    this.onMove = null
    this.onUp = null
  }

  revoke() {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = null
  }
}
